import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import GameLibraryPanel from './components/GameLibraryPanel';
import GameEditor from './components/GameEditor';
import GameUploadModal from './components/GameUploadModal';
import GameAnalytics from './components/GameAnalytics';
import BulkActionsBar from './components/BulkActionsBar';
import adminGameService from '../../utils/adminGameService';

const GameContentManagement = () => {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedGames, setSelectedGames] = useState([]);
  const [activeTab, setActiveTab] = useState('editor');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load games from database
  const loadGames = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminGameService.getAllGames();
      if (result.success) {
        // Transform the data to match the expected format
        const transformedGames = result.data.map(game => ({
          id: game.id,
          title: game.title,
          studio: game.gaming_studios?.name || 'Unknown Studio',
          category: game.game_category_mappings?.[0]?.game_categories?.name || 'Uncategorized',
          description: game.description,
          status: game.status,
          contentRating: game.rating,
          price: game.price?.toString() || '0',
          revenueShare: game.revenue_share_percentage || 70,
          featured: game.featured || false,
          uploadDate: game.created_at,
          releaseDate: game.published_at ? new Date(game.published_at).toISOString().split('T')[0] : '',
          downloads: game.play_count || 0,
          thumbnail: game.cover_image_url || '/assets/images/no_image.png',
          screenshots: game.screenshots || [],
          tags: game.tags || [],
          systemRequirements: {
            minRam: game.min_ram_gb?.toString() || '4',
            minStorage: game.min_storage_gb?.toString() || '2',
            webGpuSupport: game.webgpu_required !== false
          },
          // WebGPU specific fields
          webgpu_url: game.webgpu_url,
          webgpu_data_url: game.webgpu_data_url,
          webgpu_wasm_url: game.webgpu_wasm_url,
          webgpu_framework_url: game.webgpu_framework_url,
          webgpu_loader_url: game.webgpu_loader_url,
          webgpu_config: game.webgpu_config,
          game_type: game.game_type,
          tier: game.tier,
          // Directory-based game fields
          game_structure_type: game.game_structure_type,
          game_directory_path: game.game_directory_path,
          game_manifest: game.game_manifest
        }));
        setGames(transformedGames);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load games');
      console.error('Error loading games:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGames();
  }, []);

  const handleGameSelect = (game) => {
    setSelectedGame(game);
    setActiveTab('editor');
  };

  const handleGameSave = async (gameData) => {
    try {
      if (gameData.id && games.find(g => g.id === gameData.id)) {
        // Update existing game
        const result = await adminGameService.updateGame(gameData.id, gameData);
        if (result.success) {
          await loadGames(); // Reload games to get updated data
          setSelectedGame(gameData);
        } else {
          alert(`Failed to update game: ${result.error}`);
        }
      } else {
        // This shouldn't happen as new games are created via upload modal
        console.warn('Attempted to save new game via editor');
      }
    } catch (error) {
      console.error('Error saving game:', error);
      alert('Failed to save game');
    }
  };

  const handleGameUpload = async (newGame) => {
    // Reload games to get the latest data including the new game
    await loadGames();
    setSelectedGame(newGame);
    setActiveTab('editor');
  };

  const handleDirectoryUpload = async (uploadData) => {
    try {
      // Create a new game entry first
      const gameData = {
        title: uploadData.gameType === 'react' ? 'New React Game' : 'New WebGPU Game',
        description: uploadData.gameType === 'react' ? 'Uploaded React HTML5 game' : 'Uploaded WebGPU game',
        game_type: uploadData.gameType || 'webgpu',
        game_structure_type: 'directory',
        status: 'draft',
        tier: 'premium'
      };
      
      const createResult = await adminGameService.createGame(gameData);
      if (!createResult.success) {
        throw new Error(createResult.error);
      }
      
      const gameId = createResult.data.id;
      
      // Upload the directory structure
      const uploadResult = await adminGameService.uploadDirectoryGame(gameId, uploadData);
      if (!uploadResult.success) {
        throw new Error(uploadResult.error);
      }
      
      // Update the game with directory information
      const updateResult = await adminGameService.updateGame(gameId, {
        game_directory_path: uploadResult.data.directoryPath,
        game_manifest: uploadResult.data.manifest,
        game_structure_type: 'directory'
      });
      
      if (!updateResult.success) {
        throw new Error(updateResult.error);
      }
      
      // Reload games and select the new one
      await loadGames();
      const newGame = games.find(g => g.id === gameId);
      if (newGame) {
        setSelectedGame(newGame);
        setActiveTab('editor');
      }
    } catch (error) {
      console.error('Error uploading directory game:', error);
      alert('Failed to upload game: ' + error.message);
    }
  };

  const handleBulkAction = async (action, gameIds) => {
    try {
      switch (action) {
        case 'publish':
          await adminGameService.bulkUpdateGames(gameIds, { status: 'published', published_at: new Date().toISOString() });
          break;
        case 'unpublish':
          await adminGameService.bulkUpdateGames(gameIds, { status: 'draft' });
          break;
        case 'feature':
          // Feature functionality not implemented in database
          console.log('Feature action not implemented');
          break;
        case 'unfeature':
          // Unfeature functionality not implemented in database
          console.log('Unfeature action not implemented');
          break;
        case 'delete':
          for (const gameId of gameIds) {
            await adminGameService.deleteGame(gameId);
          }
          if (selectedGame && gameIds.includes(selectedGame.id)) {
            setSelectedGame(null);
          }
          break;
        case 'export':
          console.log('Exporting games:', gameIds);
          // TODO: Implement export functionality
          break;
        default:
          break;
      }
      
      // Reload games after bulk action
      await loadGames();
      setSelectedGames([]);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert(`Failed to perform ${action} action`);
    }
  };

  const handleBulkActionFromBar = (action) => {
    handleBulkAction(action, selectedGames);
  };

  const handleClearSelection = () => {
    setSelectedGames([]);
  };

  const tabs = [
    { id: 'editor', label: 'Game Editor', icon: 'Edit' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />
      
      {/* Main Content */}
      <div className="lg:pl-72">
        {/* Header */}
        <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Game Content Management</h1>
                <nav className="flex items-center space-x-2 text-sm text-muted-foreground mt-1">
                  <span>Admin</span>
                  <Icon name="ChevronRight" size={16} />
                  <span className="text-foreground">Game Management</span>
                </nav>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Search"
                  onClick={() => {}}
                >
                  Search
                </Button>
                <Button
                  variant="default"
                  iconName="Plus"
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  Add Game
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex h-[calc(100vh-5rem)]">
          {/* Left Panel - Game Library */}
          <div className="w-1/3 border-r border-border">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Icon name="Loader2" size={32} className="text-muted-foreground animate-spin mx-auto mb-2" />
                  <p className="text-muted-foreground">Loading games...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-2" />
                  <p className="text-destructive mb-4">{error}</p>
                  <Button variant="outline" onClick={loadGames}>
                    Retry
                  </Button>
                </div>
              </div>
            ) : (
              <GameLibraryPanel
                games={games}
                selectedGame={selectedGame}
                onGameSelect={handleGameSelect}
                onBulkAction={handleBulkAction}
              />
            )}
          </div>

          {/* Right Panel - Game Editor/Analytics */}
          <div className="flex-1 flex flex-col">
            {/* Tab Navigation */}
            {selectedGame && (
              <div className="border-b border-border">
                <div className="flex items-center space-x-1 px-6 py-3">
                  {tabs.map((tab) => (
                    <Button
                      key={tab.id}
                      variant={activeTab === tab.id ? 'default' : 'ghost'}
                      size="sm"
                      iconName={tab.icon}
                      iconPosition="left"
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Tab Content */}
            <div className="flex-1">
              {activeTab === 'editor' ? (
                <GameEditor
                  game={selectedGame}
                  onSave={handleGameSave}
                  onCancel={() => setSelectedGame(null)}
                />
              ) : (
                <GameAnalytics game={selectedGame} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <GameUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleGameUpload}
      />


      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedGames.length}
        onAction={handleBulkActionFromBar}
        onClear={handleClearSelection}
      />

      {/* Mobile Padding */}
      <div className="lg:hidden h-20" />
    </div>
  );
};

export default GameContentManagement;
